import { Measure } from '@wizeoo/akm-message-lib';
import { MatrixCallParam } from '../../manager/matrix/matrix.engine';
import { WizConstants } from '../../model/constants';
import { ComputeResult, IBaselineValue } from '../../model/interfaces';
import { FeiVO } from '../../vo/workingdata/fei.vo';
import { WidgetCell, WidgetLine } from '../../vo/workingdata/widget-line.obj';
import { DataLooper, LoopResult } from '../baselines/data.looper';
import { FunctionCoreEngine } from './function.core.engine';
import { MathUtils } from './math.utils';
import { MeasureCoreEngine } from './measure.core.engine';
import { MeasMode, MeasModeItem } from './measure.mode.obj';

/**
 * ordonnanceur de formules et calculateur
 * Totalement indépendant du Model. Tous les traitements sont effectués sur le FEI passé en paramètre
 */
export class FormulaCoreEngine {

  /**
   * création de l'entrée de map si nécessaire pour la préparation des pendings
   * @param pendings: pendingMap, la map en cours d'alimentation
   * @param level cible
   * @param scen cible
   * @returns le set de IBaselineValue
   */
  public static checkPreparePendingMapExist(pendings: Map<number, Map<string, Set<WidgetCell>>>, level: number, scen: string): Set<WidgetCell> {
    if (!pendings.has(level)) { // ajout du level cible dans la map de pendings
      pendings.set(level, new Map());
    }
    if (!pendings.get(level).has(scen)) { // ajout du scenario cible dans la map de pendings
      pendings.get(level).set(scen, new Set());
    }
    return pendings.get(level).get(scen);
  }


  /**
   * calcul des lignes (widgetLines) du FEI
   *
   * @param fei Le moteur de calcul
   * @param lines à calculer
   * @param silent true par défaut. N'affiche pas les traces
   * @params callback pour signaler que c'est VRAIMENT terminé (calcul avec matrices)
   */
  public static computeLines(fei: FeiVO, lines: WidgetLine[], silent: boolean = true, callback?: Function): void {


    // ici, on ne calcule que les FEIs monoScen. Donc les lignes sont portées par l'entrée de 
    // scenario unique de la map de lignes
    // let lines : WidgetLine[] = [...fei.lines.get(fei.scenario).values()];

    // prise des tailles
    const stepSize: number = lines.length;

    let bddTime: number = 0; // temps collecte données BDD
    let functionTime: number = 0; // temps traitement des fonctions
    let pendingTime: number = 0; // temps de calcul des steps liés
    let totalTime: number = 0; // temps global
    let startTime = new Date().getTime(); // début mesure

    // construction des ref BDDs, pour ne pas les calculer
    const persistedRefs: Set<string> = this.getPersitedMeasures(fei);

    let endTime = new Date().getTime(); // fin collecte données BDD
    bddTime = (endTime - startTime) / 1000;
    startTime = new Date().getTime(); // au tour des fonctions

    // calcul des mesures
    // FunctionCoreEngine.startFunctionTime();
    this.getLineFromOuter(fei, lines, persistedRefs);
    // FunctionCoreEngine.stopFunctionTime();
    fei.computed = true; // on marque le FEI comme calculé. Il reste peut-être des pendings, c'est pour les prochaines loop

    endTime = new Date().getTime(); // fin collecte données BDD
    functionTime = (endTime - startTime) / 1000;
   
    startTime = new Date().getTime(); // au tour des recalculs sur les autres steps
    // les recalculs effectués, par FEI
    const loopResults: Map<FeiVO, LoopResult> = new Map();
    DataLooper.doLoop(fei, loopResults); //  computePendings(fei);    
    endTime = new Date().getTime(); // fin collecte données BDD
     
    pendingTime = (endTime - startTime) / 1000;
    totalTime = bddTime + functionTime + pendingTime;

    if (!silent) {
      let message: any[] = ['step:', fei.visionStepLevel, '#', fei.scenario, '#', stepSize, 'line(s)', stepSize * fei.orderedMatrix.length, 'cells'];
      message = message.concat(['/ times:', totalTime.toPrecision(2), 's (bdd:', bddTime.toPrecision(2), 's, ', ' otherSteps:', pendingTime.toPrecision(2), 's, functions: ', functionTime.toPrecision(2), 's)']);
      console.log(message.join(' '));
    }

    // il existe des appels de matrice, il faut refaire un passage après le chargement
    if(fei.model.cellMatrixEngine.dexiePromises.size > 0) {
      this.computeWithMatrix(fei, callback);
    } else {
      if(!!callback) {
        callback(fei);
      }
    }

    
  }

  /**
   * collecte des informations de matrice
   * @param fei 
   * @param callback pour signaler que tout est OK
   */
  private static computeWithMatrix(fei: FeiVO, callback?: Function): void {

    // console.log('computeWithMatrix', fei.model.cellMatrixEngine.dexiePromises);
    Promise.all(fei.model.cellMatrixEngine.dexiePromises.values()).then((matrix: MatrixCallParam[]) => {
      matrix.forEach((mat:MatrixCallParam) => {
        // console.log('computeWithMatrix', mat);
        let cell: WidgetCell = mat.widgetCell;
        let res: any = null;
        if(mat.result?.length > 0) {
          // console.log('matrix', matrix);
          let rawResult: string = mat.result[0][mat.targetColumn];
          // correction des formats numérique
          if(rawResult !== null && rawResult!==undefined) {
            if(fei.model.dataEngine.datakindIsNumeric(mat.datakind)) {
              res = Number(rawResult.replaceAll(',','.').replaceAll(' ',''));
            } else {
              res = rawResult;
            }
          }
        }
        fei.model.cellMatrixEngine.dexieResolved.set(cell, res);
        fei.model.cellMatrixEngine.addPendingCell(cell.l, cell);
      });

      fei.model.cellMatrixEngine.dexiePromises.clear();
      // et nouveau tour de boucle
      const loopResults: Map<FeiVO, LoopResult> = new Map();
      DataLooper.doLoop(fei, loopResults); //  computePendings(fei); 
      if(!!callback) {
        callback(fei);
      } else {
        // console.log('computeWithMatrix no callback', fei);
        fei.model.dataEngine.finalizeCompute(loopResults, null); // pour dispatch de l'event  
      } 

    });
  }


  /**
   * construction de la matrice de cellule pour les données I & S (issues du cache)
   * @param fei source
   * @returns le set<measure#mode> alimenté, pour ne pas chercher à les calculer...
   */
  public static getPersitedMeasures(fei: FeiVO): Set<string> {
    // set des mesures#mode inertes (I éditables & S)
    let result: Set<string> = new Set<string>();

    // on balaie la matrice
    fei.orderedMatrix.forEach((mes: MeasMode) => {
      // mesures S sans calcul
      if (mes.mm === WizConstants.MEASURE_MODE_SUBMIT && (mes.form === null || mes.form.length === 0)) {
        result.add(mes.code);
      }

      // mesures I éditables
      if (mes.mm === WizConstants.MEASURE_MODE_INTERACT && mes.m.editable) {
        result.add(mes.code);
      }
    });

    return result;
  }

  /**
   * Construction des lignes non persistées. alimentation de la matrice
   *
   * @param fei Instance de gestion des formules
   * @param lines WidgetLine[] à enregistrer
   * @param inBdd map des lignes I / S inertes
   * @return... rien
   */
  public static getLineFromOuter(fei: FeiVO, lines: WidgetLine[], inBdd: Set<string>): void {

    const required: MeasMode[] = fei.orderedMatrix.filter(mm => !inBdd.has(mm.code)); // uniquement les mesures calculées
    let cr: ComputeResult;

    let ibav: IBaselineValue;
    required.forEach((measureMode: MeasMode) => {
      lines.forEach((wl: WidgetLine) => {
        cr = this.computeMmiDependencies(measureMode.mmi, wl, fei); 
        ibav = fei.model.cellMatrixEngine.addCell(wl.level, wl.key, wl.scen, measureMode.code, cr.valor);
      
        if(fei.translatableEntries?.has(measureMode.code)) { // mémorisation des libellés traduits si nécessaire
          // ici, c'est la génèse sinon c'est dans le looper
          ibav.vlo = {};
          ibav.vlo[fei.model.currentLocaleCode] = cr.valor;
          // console.log('trsValue', measureMode.code, cr.valor, fei.model.currentLocaleCode, ibav.vlo);
        }

      });
    });
  }

  /**
   * Calcul récursif des mmi du mmi principal du MeasureMode
   *
   * @param mmi      Etape de calcul à traiter
   * @param line     WidgetLine Ligne de travail
   * @param fei      Instance du moteur de calcul
   * @param fakeMode pour appel depuis la génération des styles / des forms pour éviter la persistence des dépendances dans CellMatrix
   * @param fakeValues injection de valeurs fictive pour pré-calcul field(mesure éditable) = value
   * @returns ComputeResult le résultat
   */
  public static computeMmiDependencies(mmi: MeasModeItem, line: WidgetLine, fei: FeiVO, fakeMode: boolean = false, fakeValues?: Map<string, string>): ComputeResult {

    // interception des fonctions COLLECT / subCollect
    if (mmi.fFunc && !!FunctionCoreEngine.checkIsCollectionFunction(fei.model, mmi.funcCode)) {
      // remplacement du content par une collection de content (mmi) calculés
      FunctionCoreEngine.callFunction(mmi.funcCode, mmi, line, fei);
      // console.log('collect', mmi, mmi.collectContent);
    } else {
      // tout sauf les fonctions collect
      if (mmi.c.length > 0) {
        // des paramètres, calcul à effectuer

        mmi.nbComp = 0; // reset du nb comparator identifiés

        mmi.c.forEach((child: MeasModeItem) => {
          // on calcule tous les enfants
          this.computeMmiChild(mmi, line, child, fei, fakeMode, fakeValues);
        }); // fin du for, tous les contents ont un résult

        // ### Phase calcul
        if (mmi.fFunc) {
          // fonction
          // console.log('func', mmi.functionCode, mmi, mmi.collectContent);
          FunctionCoreEngine.callFunction(mmi.funcCode, mmi, line, fei);
        } else {
          // calcul simple
          // présence de comparateur (isolé)
          if (mmi.nbComp <= 1) {
            // simple Math(0) ou comparaison(1), retour 1 ou 0 (Bool)
            MathUtils.computeMathParam(mmi);
          } // plus de 1 comparateur, erreur
          else {
            mmi.isErr = true;
            mmi.r = FunctionCoreEngine.FUNCTION_ERROR;
          }
        }
      } else {
        // pas d'enfants ? je suis terminal / une constante / un texte

        if (mmi.fConst) {
          // numérique
          mmi.r = mmi.form;
        } else if (mmi.ftxt) {
          // texte, on enlève les quotes
          // rien, déjà alimenté //mmi.result = mmi.rawFormula
          // mmi.setResult(mmi.getRawFormula());
        } else if (mmi.fRand) {

          if (fakeMode && fakeValues?.has(mmi.form)) {
            mmi.r = fakeValues.get(mmi.form);
          } else {
            let requiredCell: WidgetCell = line.cells.get(mmi.form);
            if (!requiredCell) {
              line.cells.set(mmi.form, { k: line.key, m: mmi.form, sc: line.scen, l: line.level });
              requiredCell = line.cells.get(mmi.form);
            }

            let targetCell: WidgetCell = line.cells.get(mmi.mm.code);
            if (!targetCell) {
              line.cells.set(mmi.mm.code, { k: line.key, m: mmi.mm.code, sc: line.scen, l: line.level });
              targetCell = line.cells.get(mmi.mm.code);
            }

            mmi.r = fei.model.cellMatrixEngine.getDependancyCellValue(requiredCell, targetCell, fakeMode); //  src.result;
          }
        } else {
          // je suis une formule S sans valeur, on doit jamais passer ici
          // baseline.values[mmi.measureMode.code]? baseline.values[mmi.measureMode.code].valor : null;
          // console.log('S me', mmi, line );
        }
      }
    }

    return {
      valor: mmi.r,
      error: mmi.isErr
    };
  }

  /**
   * Calcul d'une étape de calcul "enfant" Récursivité dans le cas d'une étape "fonction" ou "parenthèses"
   *
   * @param mmi      Etape de calcul "parent"
   * @param line Ligne de travail concernée
   * @param child    Etape de calcul "enfant" à calculer
   * @param fei      Instance de gestion des formules
   */
  private static computeMmiChild(mmi: MeasModeItem, line: WidgetLine, child: MeasModeItem, fei: FeiVO, fakeMode: boolean, fakeValues?: Map<string, string>): void {
    if (child.fTer) {
      // calcul terminé pour l'enfant
      if (child.fConst) {
        // nombre
        child.r = child.form;
      } else if (child.ftxt) {
        // texte, suppression des quotes
        // rien déjà alimenté // child.result = child.rawFormula.replace(/"/g, '');
      } else if (child.fRand) {

        if (fakeMode && fakeValues?.has(child.form)) {
          child.r = fakeValues.get(child.form);
        } else {
          if(!line.cells) { // fix, pas de cells ??
            // console.log('pivotLine no cells', line, child.form);
            line.cells = new Map();
          }
          let requiredCell: WidgetCell = line.cells.get(child.form);
          if (!requiredCell) {
            line.cells.set(child.form, { k: line.key, m: child.form, sc: line.scen, l: line.level });
            requiredCell = line.cells.get(child.form);
          }
          let targetCell: WidgetCell = line.cells.get(mmi.mm.code);
          if (!targetCell) {

            line.cells.set(mmi.mm.code, { k: line.key, m: mmi.mm.code, sc: line.scen, l: line.level });
            targetCell = line.cells.get(mmi.mm.code);
          }

          child.r = fei.model.cellMatrixEngine.getDependancyCellValue(requiredCell, targetCell, fakeMode);
        }


      } else if (child.fComp) {
        // comparateur, on incrémente le compteur sur le parent (1 seul sera autorisé)
        mmi.nbComp = mmi.nbComp + 1;
      } else if (!child.fOper) {
        // ici, il ne doit rester que les opérateurs, qui seront gérés lors du calcul global
        // donc on doit pas passer ici...
        console.warn('unknow terminal part: {}', mmi.form);
      }
    } else {
      // pas terminal. C'est une fonction ou des parenthèses
      // Fonction ou parenthèses
      this.computeMmiChildFunctionOrParenthesis(mmi, line, child, fei, fakeMode, fakeValues);
    }
  }

  /**
   * Calcul d'une étape de calcul fonction ou parenthèses Récursivité dans ce cas
   *
   * @param mmi      Etape de calcul "parent"
   * @param line Ligne de travail concernée
   * @param child    Etape de calcul "enfant" à calculer
   * @param fei      Instance de gestion des formules
   */
  private static computeMmiChildFunctionOrParenthesis(
    mmi: MeasModeItem,
    line: WidgetLine,
    child: MeasModeItem,
    fei: FeiVO,
    fakeMode: boolean = false,
    fakeValues?: Map<string, string>
  ): void {
    if (child.fSis) {
      // parenthèses, calcul des enfants de l'enfant unique
      const childMeasureModeItem: MeasModeItem = child.c[0];
      // dans le cas des parenthèses, l'enfant contient la formule sans parenthèses
      this.computeMmiDependencies(childMeasureModeItem, line, fei, fakeMode, fakeValues);
      // donc prise du résultat de l'enfant
      child.r = childMeasureModeItem.r;

      if (childMeasureModeItem.isErr) {
        // ici. Si variable n'existe pas
        // console.error('Missing children for calculation on parenthesis');
        child.isErr = true;
        mmi.isErr = true;
        mmi.r = childMeasureModeItem.r;
      }

      // if (mmi.rawFormula) {
      //   baseline.mmiMap.set(mmi.rawFormula, mmi.result);
      //   // console.log('mmifuncOrparent', mmi.rawFormula, mmi.result);
      // }
    } else if (child.fFunc) {
      // fonction
      // calcul des enfants (paramètres de la fonction) . La fonction sera calculée lors de la phase...calcul
      // console.log('funct', mmi.rawFormula, mmi.measureMode.measure.code);
      // if (mmi.measureMode.measure.code === 'MES_MIN_COEF_EDIT_RESTRICT') {
      //   console.log('funct', mmi.rawFormula, mmi.measureMode.measure.code);
      // }

      this.computeMmiDependencies(child, line, fei, fakeMode, fakeValues);
    } else {
      // rien ? je suis un paramètre de fonction
      this.computeMmiDependencies(child, line, fei, fakeMode, fakeValues);
    }
  }

  /* #############################################################
         calculs sur FakeFEI pour les séries
     ############################################################# */

  /**
   * calcul d'un élément sans l'ajouter à la matrice du FEI (utilisé dans les styles conditionnels et les séries)
   * @param fei porteur
   * @param formula la formule à calculer
   * @param wl: WidgetLine
   * @return un mesureMode "fake"
   */
  public static computeFreeMeasure(fei: FeiVO, formula: string, wl: WidgetLine): string {
    const mm: MeasMode = MeasureCoreEngine.LightFakeMeasureMode(fei,
      WizConstants.FAKE_FREE,
      WizConstants.MEASURE_MODE_SUBMIT,
      '=' + formula);
    const cr: ComputeResult = FormulaCoreEngine.computeMmiDependencies(mm.mmi, wl, fei, true);
    return cr.valor;
  }

  /**
   * calcul de la ligne pour identifier les triggers en récursif
   *
   * @param fei Le moteur de calcul
   * @param line à calculer
   * 
   * @result la liste des mesures modifiées
   */
  public static checkRecurseTriggers(fei: FeiVO, wl: WidgetLine): Map<string, [string, string]> {

    let updatedMeasures: Map<string, [string, string]> = new Map();

    // construction des ref BDDs, pour ne pas les calculer
    const persistedRefs: Set<string> = this.getPersitedMeasures(fei);
    const required: MeasMode[] = fei.orderedMatrix.filter(mm => !persistedRefs.has(mm.code)); // uniquement les mesures calculées
    
    let cr: ComputeResult;
    let ibav: IBaselineValue;
    let newVal: string;
    let oldVal: string;

    required.forEach((measureMode: MeasMode) => {
      // if(wl.key === '152' && measureMode.code === 'M_COLLM#I') {
      //   console.log('M_COLLM#I trap');
      // }  
      cr = this.computeMmiDependencies(measureMode.mmi, wl, fei); 

      
      oldVal = fei.model.cellMatrixEngine.getCellValue(wl.level, wl.key, wl.scen, measureMode.code);
      newVal = cr?.valor;

      // if(wl.key === '153' && measureMode.code === 'M_CL2#I') {
      //   console.log('M_COLLM#I', oldVal, newVal);
      // } 

      if(oldVal != newVal) { // != pour gestion des null ?
        updatedMeasures.set(measureMode.code, [oldVal, newVal]);
      }

      ibav = fei.model.cellMatrixEngine.addCell(wl.level, wl.key, wl.scen, measureMode.code, cr.valor);
    
    });

    // les recalculs effectués, par FEI
    const loopResults: Map<FeiVO, LoopResult> = new Map();

    // datalooper en mode updatedMeasures.add(measureMode.code);
    DataLooper.doLoop(fei, loopResults, updatedMeasures); //  computePendings(fei);      
    // console.log('checkRecurseTriggers', updatedMeasures);

    return updatedMeasures;
    
  }


}
